import { useCallback, useEffect, useRef } from "react";
import Konva from "konva";
import { useAtomValue } from "jotai";
import { stageRefAtom } from "state-atoms/general-atoms";

interface UseMondayTaskInteractionCallbacks {
  onDragMove?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onMouseDown?: () => void;
  onMouseUp?: () => void;
}

interface UseMondayTaskInteractionOptions {
  elementRef: React.RefObject<Konva.Node>;
  onClick?: () => void;
  disabled?: boolean;
  callbacks?: UseMondayTaskInteractionCallbacks;
}

/**
 * Hook for handling click interactions on Monday task elements
 * that need to distinguish between clicks and drags.
 *
 * Returns handlers and state for:
 * - Preventing click actions when dragging
 * - Managing cursor state
 */
export function useMondayTaskInteraction({
  elementRef,
  onClick,
  disabled = false,
  callbacks,
}: UseMondayTaskInteractionOptions) {
  const stageRef = useAtomValue(stageRefAtom);
  const stage = stageRef?.current || null;

  const isMouseDownRef = useRef(false);
  const mouseDownPosRef = useRef({ x: 0, y: 0 });
  const hasMovedRef = useRef(false);

  useEffect(() => {
    if (!stage || !elementRef.current) return;

    const handleDragMove = (e: Konva.KonvaEventObject<DragEvent>) => {
      // Only care if mouse was pressed on our element
      if (!isMouseDownRef.current) return;

      // Check if the element being dragged is an ancestor of our elementRef
      const draggedNode = e.target;
      let node: Konva.Node | null = elementRef.current;
      while (node) {
        if (node === draggedNode) {
          // The dragged element is our parent - mark as moved
          hasMovedRef.current = true;
          // Call custom drag handler if provided
          callbacks?.onDragMove?.();
          break;
        }
        node = node.getParent();
      }
    };

    stage.on("dragmove", handleDragMove);

    return () => {
      stage.off("dragmove", handleDragMove);
    };
  }, [stage, elementRef, callbacks]);

  const onMouseEnter = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      if (disabled || isMouseDownRef.current) return;

      const container = e.target.getStage()?.container();
      if (container) {
        container.style.cursor = "pointer";
      }

      callbacks?.onMouseEnter?.();
    },
    [disabled, callbacks]
  );

  const onMouseLeave = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      const container = e.target.getStage()?.container();
      if (container) {
        container.style.cursor = "default";
      }

      callbacks?.onMouseLeave?.();
    },
    [callbacks]
  );

  const onMouseDown = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      if (disabled) return;

      isMouseDownRef.current = true;
      hasMovedRef.current = false;

      const pos = e.target.getStage()?.getPointerPosition();
      if (pos) {
        mouseDownPosRef.current = { x: pos.x, y: pos.y };
      }

      callbacks?.onMouseDown?.();
    },
    [disabled, callbacks]
  );

  const onMouseUp = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      if (disabled || !onClick) {
        isMouseDownRef.current = false;
        hasMovedRef.current = false;
        callbacks?.onMouseUp?.();
        return;
      }

      const pos = e.target.getStage()?.getPointerPosition();

      // Only trigger click if mouse hasn't moved much
      if (pos && !hasMovedRef.current) {
        const threshold = 5; // pixels
        const dx = Math.abs(pos.x - mouseDownPosRef.current.x);
        const dy = Math.abs(pos.y - mouseDownPosRef.current.y);

        if (dx < threshold && dy < threshold) {
          e.cancelBubble = true;
          onClick();
        }
      }

      isMouseDownRef.current = false;
      hasMovedRef.current = false;
      callbacks?.onMouseUp?.();
    },
    [disabled, onClick, callbacks]
  );

  return { onMouseEnter, onMouseLeave, onMouseDown, onMouseUp, isDragging: hasMovedRef.current };
}
